import { Avatar as MuiAvatar, AvatarProps as MuiAvatarProps } from '@mui/material'
import { useMemo } from 'react'

export interface InitialsAvatarProps extends Omit<MuiAvatarProps, 'children'> {
  fullName: string
  size?: number
}

interface AvatarProps extends Omit<MuiAvatarProps, 'children'> {
  text: string
  size?: number
}
export const Avatar = (props: AvatarProps) => {
  const { text, size = 24, sx = {}, ...avatarProps } = props

  const backgroundColor = useMemo(() => (avatarProps.src ? 'transparent' : 'secondary.main'), [avatarProps.src])

  // Magic number so the biggest initials fit the circle nicely (tested with initials M and MM)
  const fontSizeRatio = text.length === 1 ? 0.68 : 0.525

  return (
    <MuiAvatar
      sx={{
        width: size,
        height: size,
        fontSize: fontSizeRatio * size,
        backgroundColor,
        ...sx,
      }}
      {...avatarProps}
    >
      {text}
    </MuiAvatar>
  )
}

export const InitialsAvatar: React.FC<InitialsAvatarProps> = ({ fullName, ...props }) => {
  // TODO: We've experienced errors where `fullName` can be `undefined`. Further investigation is needed.
  // Defaulting to an empty string while investigation is on-going to prevent crashes.
  // Sentry Error: https://omnea.sentry.io/share/issue/529478ca0e024b468eb4f070d84238b0/
  const initials = useMemo(() => getInitials(fullName ?? ''), [fullName])
  return <Avatar data-testid={`user-avatar-${fullName}`} {...props} text={initials} />
}

// Regex for non-alphanumeric or space characters
const nonAlphanumericRegex = /[^a-zA-Z0-9]/g

export function getInitials(fullName: string) {
  const [firstName, ...surnames] = fullName.trim().split(' ')

  const firstInitial = firstName?.replace(nonAlphanumericRegex, '').charAt(0) ?? ''
  const lastInitial = surnames[surnames.length - 1]?.replace(nonAlphanumericRegex, '').charAt(0) ?? ''

  return `${firstInitial}${lastInitial}`.toUpperCase() || '!'
}
